import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import ILegendLayer, { ILayerGroup } from '../model/ILegendLayer';
import MikeVisualizerLib from '../MikeVisualizer/lib/MikeVisualizer';
import { IRenderElement } from '../MikeVisualizer/lib/IMikeVisualizerModels';
import initialLayerGroups, { BASEMAP_ID, getLayerOrderIndex, layerGroupTitleBackground, layerGroupTitleMeasures, NEW_MEASURES_ID, NewMeasureLegendLayer, RIVER_ID } from '../model/layerGroups';
import MIKE_COLORS from '../styles/mike-colors';
import { OSM_COPYRIGHT } from '../MikeVisualizer/MikeBaseMapConfigurations';

export interface ILegendState {  
  drawing: boolean;
  layerGroups: Array<ILayerGroup>;
  areaLayersLoaded: boolean;
}

const initState = { 
  drawing: false,
  layerGroups: initialLayerGroups,
  areaLayersLoaded: false
} as ILegendState  

const legendSlice = createSlice({
  name: 'legend',
  initialState: initState as ILegendState,
  reducers: {  
    resetLegend: (): ILegendState => {     
      return initState as ILegendState;
    },
    showNewLegendLayer: (state, action: PayloadAction<boolean>) : ILegendState => { 
      const show = action.payload;
      const layerId = NEW_MEASURES_ID;
      const layerGroup = state.layerGroups.find((l: ILayerGroup) => l.title === layerGroupTitleMeasures);
      if (layerGroup !== undefined){
        const clearedLayers = layerGroup.layer.filter((l: ILegendLayer) => l.id !== layerId);
        const updatedLayers = show ? [NewMeasureLegendLayer, ...clearedLayers] : clearedLayers
        const updatedLayerGroup = {...layerGroup, layer: updatedLayers}
        return {...state, layerGroups: state.layerGroups.map((l: ILayerGroup) => {return l.title === layerGroupTitleMeasures ? updatedLayerGroup : l})}
      }
      return state;

    }, 
    setAreaLayersLoaded: (state, action: PayloadAction<Array<ILayerGroup>>): ILegendState => {     
      return {...state, areaLayersLoaded: true, layerGroups: [...state.layerGroups, ...action.payload, {
        title: layerGroupTitleBackground,
        hideAllLayers: false,
        layer: [
          { groupTitle: layerGroupTitleBackground, title: "Risikogewässer", id: RIVER_ID, visible: true,isTwoD: true,loaded: true, order: getLayerOrderIndex(RIVER_ID), color: MIKE_COLORS.BRANDBLUE_DEFAULT, isPoint: false}, 
          { groupTitle: layerGroupTitleBackground, title: "Hintergrundkarte", id: BASEMAP_ID, visible: true,isTwoD: true,loaded: true, order:-1}, 
        ]
      } ]};
    }, 
    drawingEnabled: (
      state,
      action: PayloadAction<boolean>
    ): ILegendState => {
      const { enable2DPointDrawing, disable2DPointDrawing } = MikeVisualizerLib
      if (action.payload){
        enable2DPointDrawing(true);
      }
      else{
        disable2DPointDrawing();
      }
      return {...state, drawing: action.payload};
    },
    applyMeasureVisibility: (
      state,
      action: PayloadAction<string>
    ): ILegendState => {
     
      const layerId = action.payload;
      const layerGroup = state.layerGroups.find((l: ILayerGroup) => l.title === layerGroupTitleMeasures);
      const layers = layerGroup ? layerGroup.layer : null
      
      if (layers){
        const { hide2DElements,getState } = MikeVisualizerLib
        const { hidden2DElementIds }  = getState();
        const layer = layers.find((l: ILegendLayer) => l.id === layerId);
        if (layer && !layer.visible){
          hide2DElements([...hidden2DElementIds, layerId])
        }
      }                 
      
      return state;
    },
    legendGroupShowAll: (
      state,
      action: PayloadAction<ILayerGroup>
    ): ILegendState => {        
      const layerGroup = action.payload;
      const title = layerGroup.title;
      const index = state.layerGroups.findIndex((l: ILayerGroup) => l.title === title);
      if (index !== -1){
         const {  
        show2DElements
       } = MikeVisualizerLib
        const layer = layerGroup.layer;
        const twoDLayerIds = layer.filter((l: ILegendLayer) => l.isTwoD === true && l.id !== BASEMAP_ID).map((l: ILegendLayer) => l.id);  
        show2DElements(twoDLayerIds);
        if (layerGroup.title === layerGroupTitleBackground){
          MikeVisualizerLib.setViewerBaseMapUrl('https://tile.openstreetmap.org/{z}/{x}/{y}.png', [OSM_COPYRIGHT])
        }
        const updatedLayers: Array<ILegendLayer> = layerGroup.layer.map((l: ILegendLayer) => {return {...l, visible: true}}  )
        const updatedLayerGroup = {...layerGroup, hideAllLayers: false, layer: updatedLayers}
        return {...state, layerGroups: state.layerGroups.map((l: ILayerGroup) => {return l.title === updatedLayerGroup.title ? updatedLayerGroup : l})}
      }
      return state
    }, 
    legendGroupHideAll: (
      state,
      action: PayloadAction<ILayerGroup>
    ): ILegendState => {        
      const layerGroup = action.payload;
      const title = layerGroup.title;
      const index = state.layerGroups.findIndex((l: ILayerGroup) => l.title === title);
      if (index !== -1){
      const {   
        hide2DElements,
        getState
       } = MikeVisualizerLib
        const { hidden2DElementIds }  = getState();
        const layer = layerGroup.layer;
        const twoDLayerIds = layer.filter((l: ILegendLayer) => l.isTwoD === true && l.id !== BASEMAP_ID).map((l: ILegendLayer) => l.id);       
        hide2DElements([...hidden2DElementIds, ...twoDLayerIds]);
         if (layerGroup.title === layerGroupTitleBackground){
          MikeVisualizerLib.setViewerBaseMapUrl('')
        }
        const updatedLayers: Array<ILegendLayer> = layerGroup.layer.map((l: ILegendLayer) => {return {...l, visible: false}}  )
        const updatedLayerGroup = {...layerGroup, hideAllLayers: true, layer: updatedLayers}
        return {...state, layerGroups: state.layerGroups.map((l: ILayerGroup) => {return l.title === updatedLayerGroup.title ? updatedLayerGroup : l})}
      }
      return state
    }, 
    legendGroupNameHideAll: (
      state,
      action: PayloadAction<string>
    ): ILegendState => {       
   
      const title = action.payload;
      const layerGroup = state.layerGroups.find((l: ILayerGroup) => l.title === title);
      if (layerGroup !== undefined){
      const {  
        hide2DElements,
        getState
       } = MikeVisualizerLib
        const { hidden2DElementIds }  = getState();
        const layer = layerGroup.layer;
        const twoDLayerIds = layer.filter((l: ILegendLayer) => l.isTwoD === true && l.id !== BASEMAP_ID).map((l: ILegendLayer) => l.id);    
        hide2DElements([...hidden2DElementIds, ...twoDLayerIds]);
         if (layerGroup.title === layerGroupTitleBackground){
          MikeVisualizerLib.setViewerBaseMapUrl('')
        }
        const updatedLayers: Array<ILegendLayer> = layerGroup.layer.map((l: ILegendLayer) => {return {...l, visible: false}}  )
        const updatedLayerGroup = {...layerGroup, hideAllLayers: true, layer: updatedLayers}
        return {...state, layerGroups: state.layerGroups.map((l: ILayerGroup) => {return l.title === updatedLayerGroup.title ? updatedLayerGroup : l})}
      }
      return state
    },    
    layerLoaded: (
      state,
      action: PayloadAction<ILegendLayer>
    ): ILegendState => {
      const layer = action.payload   
      const updatedItems = state.layerGroups.map((group) => {
          return group.title === layer.groupTitle && group.layer.findIndex((l: ILegendLayer) => l.id == layer.id) !== -1 ? {...group, layer: [...group.layer, layer]} : group}
      ) 
      return {...state, layerGroups: updatedItems}
    },  
    layerHidden: (
      state,
      action: PayloadAction<ILegendLayer>
    ): ILegendState => {
      const layer = action.payload
      const { getState } = MikeVisualizerLib;
      const { hidden2DElementIds } = getState();
      if (layer.isTwoD){
        if (layer.id === BASEMAP_ID){
          MikeVisualizerLib.setViewerBaseMapUrl("")
        }
        else{
          MikeVisualizerLib.hide2DElements([...hidden2DElementIds, layer.id])
        }        
      } else{
        MikeVisualizerLib.hideElement(layer.id)
      } 

      const updatedItems = state.layerGroups.map((group) => {
        return group.title === layer.groupTitle ? {...group, layer: group.layer.map((l: ILegendLayer) => {return l.id === layer.id ? {...l, visible: false} : l}  )} : group}
      ) 
      return {...state, layerGroups: updatedItems}
    },     
    layerShown: (
      state,
      action: PayloadAction<ILegendLayer>
    ): ILegendState => {
      const layer = action.payload
      if (layer.isTwoD)  {
        if (layer.id === BASEMAP_ID){
          MikeVisualizerLib.setViewerBaseMapUrl('https://tile.openstreetmap.org/{z}/{x}/{y}.png', [OSM_COPYRIGHT])
        }
        else{
          const { getState, show2DElements } = MikeVisualizerLib;
          const { hidden2DElementIds, rendered2DElements } = getState();
          const newHidden = hidden2DElementIds.filter((id: string) => id !== layer.id);
          const renderedIds = rendered2DElements.map((re: IRenderElement) => re.id)
          const newRenderedIds = renderedIds.filter((id: string) => !newHidden.includes(id))
          show2DElements(newRenderedIds) 
        }   
      }
      else{
        MikeVisualizerLib.showElement(layer.id)
      }
      const layerGroup = state.layerGroups.find((group) => {
        return group.title === layer.groupTitle;
      });
      if (layerGroup !== undefined) {
        const updatedLayers: Array<ILegendLayer> = layerGroup.layer.map((l: ILegendLayer) => {return l.id === layer.id ? {...l, visible: true} : l}  )
        const updatedItems = state.layerGroups.map((group) => {
          return group.title === layerGroup.title ? {...layerGroup, layer: updatedLayers} : group}
        )        
        return {...state, layerGroups: updatedItems}
      }
      return state   
    }  
  }, 
  selectors: {
    measureLayerGroup: (state) => state.layerGroups.find((lg: ILayerGroup) => lg.title === layerGroupTitleMeasures)
  }
})

export const {
  measureLayerGroup
} = legendSlice.selectors;

export const {
  layerShown, 
  layerHidden,
  layerLoaded,
  drawingEnabled,
  applyMeasureVisibility,
  legendGroupShowAll,
  legendGroupHideAll,
  resetLegend,
  setAreaLayersLoaded,
  showNewLegendLayer,
  legendGroupNameHideAll
} = legendSlice.actions

export default legendSlice.reducer

